import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { CategoriesItemComponent } from '@app/library/panes/categories-content-filter/categories-item/categories-item.component';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { AccountService, ContentService, FeaturesService } from '@core/services';

import { CLibraryFilterData } from '@store/models';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import { getMe } from '@store/reducers/profile.reducer';
import {
  ContentTypeEnum,
  EContentPaneFormat,
  EContentPanelRoute,
  EntityTypeEnum,
  FeatureEnum,
  IContentFilterBlock,
  Router as BaseRouter,
  Subject as BaseSubject,
  SubjectCategoryEnum,
  User,
} from 'lingo2-models';
import { filter, first, takeUntil } from 'rxjs/operators';
import { AbstractContentPane } from '../models/abstract-content-pane';
import {
  customPagesForFavorites,
  customPagesForFeed,
  customPagesForMyClasses,
  customPagesForMyClassesForTutors,
  customPagesForMyLibrary,
  customPagesForMyLibraryForTutors,
} from './categories-content-filter.variables';

enum EFilter {
  all,
  foreign,
  native,
  others,
}

@Component({
  selector: 'app-categories-content-filter',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    RouterModule,
    LibraryElementsModule,
    SpinnersModule,
    CategoriesItemComponent,
  ],
  templateUrl: './categories-content-filter.component.html',
  styleUrls: ['./categories-content-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoriesContentFilterComponent extends AbstractContentPane implements OnInit {
  protected features = inject(FeaturesService);
  protected readonly router = inject(Router);

  @ViewChild('scroller', { read: CdkVirtualScrollViewport }) scroller: CdkVirtualScrollViewport;
  public itemSize = 166;
  public isDragScrollEnabled: boolean;
  public baseUrl: string;
  public filterActive: EFilter = EFilter.all;
  public eFilter = EFilter;
  public eContentPaneFormat = EContentPaneFormat;
  public displayMode: 'default' | 'simple' = 'default';
  private readonly contentService = inject(ContentService);

  private _libraryFilterData: CLibraryFilterData;
  private _me: User;

  ngOnInit() {
    this.isDragScrollEnabled = this.deviceService.isDesktop();
    this.store
      .select(getLibraryRouteData)
      .pipe(
        filter((d) => !!d),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (data) => {
          this._libraryFilterData = data;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  onMakeFavorite(panel: IContentFilterBlock) {
    panel.is_favorite = !panel.is_favorite;
    this.contentService
      .setBookmark(panel.id.toString(), panel.is_favorite, EntityTypeEnum.subject)
      .pipe(first(), takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  hasContent(): boolean {
    return !!this._pane?.filters?.length || this.isCustomPage();
  }

  protected onContentLoaded(viaQuery?: boolean) {
    this.isVisible = this.isCustomPage() ? true : !!this.filterPanels.length;
    this.displayMode = this._pane?.displayMode;
  }

  public routeLinkByItem(panel: IContentFilterBlock) {
    this.libRouter.setContentTypeByItem(panel.item as ContentTypeEnum);
    return this.libRouter.getLink();
  }

  public routeLinkByCustomPage(panel: IContentFilterBlock) {
    this.libRouter.setBaseRoute(panel.code as EContentPanelRoute);
    return this.libRouter.getLink();
  }

  public isCustomPage(): boolean {
    const segmentBaseRoute = this.libRouter.getLink().path?.split('/')[1];
    return ['my-classes', 'my-library', 'favorites', 'my-feed'].includes(segmentBaseRoute);
  }

  public customPages(): IContentFilterBlock[] {
    const segmentBaseRoute = this._libraryFilterData?.baseRoute?.split('/')[0];
    switch (segmentBaseRoute) {
      case 'my-classes':
        return this.filterMyClassesTabs(
          AccountService.isAsIfTeacher(this._me) ? customPagesForMyClassesForTutors : customPagesForMyClasses,
        );
      case 'my-library':
        return this.filterMyLibraryTabs(
          AccountService.isAsIfTeacher(this._me) ? customPagesForMyLibraryForTutors : customPagesForMyLibrary,
        );
      case 'favorites':
        return customPagesForFavorites;
      case 'my-feed':
        return customPagesForFeed;
    }
  }

  public filterMyClassesTabs(tabs: IContentFilterBlock[]) {
    if (!this.features.isAvailable(FeatureEnum.my_classes_homework)) {
      tabs.find((el) => el.code === String(EContentPanelRoute['my-classes/homework'])).is_visible = false;
    }

    if (!this.features.isAvailable(FeatureEnum.my_classes_schools)) {
      tabs.find((el) => el.code === String(EContentPanelRoute['my-classes/schools'])).is_visible = false;
    }

    if (AccountService.isAsIfTeacher(this._me)) {
      if (!this.features.isAvailable(FeatureEnum.my_classes_tutors)) {
        tabs.find((el) => el.code === String(EContentPanelRoute['my-classes/tutors'])).is_visible = false;
      }
    }

    return tabs.filter((el) => el.is_visible);
  }

  public filterMyLibraryTabs(tabs: IContentFilterBlock[]) {
    if (!this.features.isAvailable(FeatureEnum.my_library_services)) {
      const tab = tabs.find((el) => el.code === String(EContentPanelRoute['my-library/services']));
      if (tab) {
        tab.is_visible = false;
      }
    }

    if (!this.features.isAvailable(FeatureEnum.my_library_collections)) {
      const tab = tabs.find((el) => el.code === String(EContentPanelRoute['my-library/collections']));
      if (tab) {
        tab.is_visible = false;
      }
    }

    return tabs.filter((el) => el.is_visible);
  }

  public onActiveTabSet(code: string) {
    if (this.deviceService.isMobile()) {
      if (code && this._pane.filters) {
        const contentType = BaseRouter.getContentTypeByName(code);
        const index = this._pane.filters.findIndex((el) => el.id === Number(contentType));
        this._pane.filters[index].order = 0;
      }
    }
  }

  public get isContentType() {
    return this.filterPanels.length ? this.filterPanels[0].type === 'contentType' : false;
  }

  moveLeft() {
    this.scroller.scrollToIndex(0, 'smooth');
  }

  moveRight() {
    const offset = this.scroller.measureScrollOffset('left');
    this.scroller.scrollToOffset(offset + this.itemSize * 3, 'smooth');
  }

  public filterSubjects() {
    switch (this.filterActive) {
      case EFilter.foreign:
        return this.filterPanels.filter(
          (_s: IContentFilterBlock) => _s.item['category_id'] === SubjectCategoryEnum.foreign_language,
        );
      case EFilter.native:
        return this.filterPanels.filter(
          (_s: IContentFilterBlock) => _s.item['category_id'] === SubjectCategoryEnum.native_language,
        );
      case EFilter.others:
        return this.filterPanels.filter(
          (_s: IContentFilterBlock) => _s.item['category_id'] === SubjectCategoryEnum.non_categorized,
        );
      default:
        return this.filterPanels || [];
    }
  }

  public filteredPanels(panels: IContentFilterBlock[]): IContentFilterBlock[] {
    if (this.displayMode !== 'simple') {
      return panels.filter((_p) => _p.total_count > 0);
    }
    return panels;
  }

  public trackByFn(index, item: BaseSubject | IContentFilterBlock): number {
    return item.id;
  }
}
